import React from 'react'
import { makeStyles, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import SendIcon from '@material-ui/icons/Send'
import { semanticPalette, secondaryPalette, brandPalette } from '@paintscout/ui/src/styles/palette'
import type { Payment } from 'paintscout'

export const LABEL: Record<Payment['status'], string> = {
  pending: 'Requested',
  paid: 'Successful',
  refunded: 'Refunded',
  failed: 'Failed',
  processing: 'Processing'
}

const useStyles = makeStyles<Theme, PaymentStatusBadgeProps>(
  () => ({
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '0px 6px',
      borderRadius: 4,
      border: '1px solid',
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '20px',
      whiteSpace: 'nowrap'
    },
    banner: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 12px',
      borderRadius: 6,
      border: '1px solid',
      width: '100%',
      boxSizing: 'border-box'
    },
    icon: {
      fontSize: '12px !important'
    },
    bannerIcon: {
      fontSize: '16px !important'
    },
    paid: {
      backgroundColor: secondaryPalette['140'].C95,
      borderColor: semanticPalette.Success.S60,
      color: semanticPalette.Success.S50
    },
    refunded: {
      backgroundColor: brandPalette.Neutral.P94,
      borderColor: brandPalette.Neutral.P70,
      color: brandPalette.Neutral.P40
    },
    processing: {
      backgroundColor: brandPalette.Neutral.P96,
      borderColor: brandPalette.Neutral.P80,
      color: brandPalette.Neutral.P50
    },
    pending: {
      backgroundColor: semanticPalette.Warning.S95,
      borderColor: semanticPalette.Warning.S80,
      color: semanticPalette.Warning.S70
    },
    failed: {
      backgroundColor: semanticPalette.Danger.S95,
      borderColor: semanticPalette.Danger.S60,
      color: semanticPalette.Danger.S60
    }
  }),
  { name: 'PaymentStatusBadge' }
)

export interface PaymentStatusBadgeProps {
  classes?: StyleClasses<typeof useStyles>
  status: Payment['status']
  message?: string
  /** Replaces the status's default text, e.g. "Cancelled" for a failed Wisetack payout. Colors still follow status. */
  label?: string
}

// pending could be <i class="fa-solid fa-paper-plane-top"></i> but I'm keeping the send icon so it matches elsewhere in the app
function PaymentStatusBadge(props: PaymentStatusBadgeProps) {
  const { status, message, label } = props
  const { __ } = useTranslation()
  const classes = useStyles(props)
  const badgeIcon = {
    paid: <i className={`fa-solid fa-check ${classes.icon}`} />,
    refunded: <i className={`fa-solid fa-arrow-u-turn-up-left ${classes.icon}`} />,
    processing: <i className={`fa-solid fa-spinner ${classes.icon}`} />,
    pending: <SendIcon className={classes.icon} />,
    failed: <i className={`fa-solid fa-circle-xmark ${classes.icon}`} />
  }[status]

  const bannerIcon = {
    paid: <i className={`fa-solid fa-check ${classes.bannerIcon}`} />,
    refunded: <i className={`fa-solid fa-arrow-u-turn-up-left ${classes.bannerIcon}`} />,
    processing: <i className={`fa-solid fa-spinner ${classes.bannerIcon}`} />,
    pending: <SendIcon className={classes.bannerIcon} />,
    failed: <i className={`fa-solid fa-circle-xmark ${classes.bannerIcon}`} />
  }[status]

  if (message) {
    return (
      <div className={`${classes.banner} ${classes[status]}`}>
        {bannerIcon}
        <span>{message}</span>
      </div>
    )
  }

  return (
    <span className={`${classes.root} ${classes[status]}`}>
      {__(label ?? LABEL[status])}
      {badgeIcon}
    </span>
  )
}

export default PaymentStatusBadge
